import type { CSSProperties } from 'react';

// Authoritative "T." monogram mark tile (BRAND_MARK.md, FE-010 / FE-010a).
// Letterforms set in Inter (Inter 700 at -0.04em tracking) per owner decision (2026-10-12).
// The tile inverts with the theme: a primary-content tile (currentColor)
// with a canvas-colored letterform (var(--color-bg-canvas)).
// The dot is Inter's period glyph standing alone beside the baseline and is NEVER connected to the T.
// Tile corner radius is 0.23 of the tile edge; mark cap height is 0.40 of tile edge.
// Below 24px, the pixel-grid 16-unit variant is rendered instead of scaling the 100-unit tile.

export interface TraceMarkProps {
  size?: number;
  label?: string;
  className?: string;
  style?: CSSProperties;
}

export const TRACE_MARK_PATH_100 =
  'M26.85 37.05L26.85 30.26L59.52 30.26L59.52 37.05L47.31 37.05L47.31 70.26L39.09 70.26L39.09 37.05ZM68.52 70.77Q66.56 70.77 65.21 69.43Q63.87 68.11 63.87 66.15Q63.87 64.19 65.21 62.85Q66.56 61.54 68.52 61.54Q70.48 61.54 71.82 62.85Q73.16 64.19 73.16 66.15Q73.16 68.11 71.82 69.43Q70.48 70.77 68.52 70.77Z';

export const TRACE_MARK_PATH_16 =
  'M4.3 5.93L4.3 4.84L9.53 4.84L9.53 5.93L7.57 5.93L7.57 11.24L6.26 11.24L6.26 5.93ZM10.97 11.32Q10.65 11.32 10.44 11.11Q10.22 10.9 10.22 10.58Q10.22 10.27 10.44 10.05Q10.65 9.84 10.97 9.84Q11.28 9.84 11.49 10.05Q11.71 10.27 11.71 10.58Q11.71 10.9 11.49 11.11Q11.28 11.32 10.97 11.32Z';

export function TraceMark({ size = 32, label, className, style }: TraceMarkProps) {
  const isSmall = size < 24;
  const a11yProps = label
    ? { role: 'img' as const, 'aria-label': label }
    : { 'aria-hidden': true as const, focusable: 'false' as const };

  if (isSmall) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 16 16"
        className={className}
        style={style}
        {...a11yProps}
      >
        <rect width="16" height="16" rx="4" fill="currentColor" />
        <path d={TRACE_MARK_PATH_16} fill="var(--color-bg-canvas)" />
      </svg>
    );
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      className={className}
      style={style}
      {...a11yProps}
    >
      <rect width="100" height="100" rx="23" fill="currentColor" />
      <path d={TRACE_MARK_PATH_100} fill="var(--color-bg-canvas)" />
    </svg>
  );
}
